import {useMemo, useEffect, useCallback} from 'react';
import {useQuery} from 'react-query';
import isEmpty from 'lodash/isEmpty';

import {SurveySlug} from 'client/common/types';

import {useSurveySlug} from 'client/common/contexts/slug';

import cleanDeep from 'client/common/utils/clean-deep';

import {AudienceSizeDO, AudienceTargeting} from 'client/common/utils/audience';
import {
    Errors,
    ERRORS_KEY,
    scrollToError,
    setError,
    SKIP_QUESTION_VALIDATION_BOX_TYPES,
    unsetError,
    validateByBoxMap,
    validateQuestions,
    ValidationErrorType
} from 'client/common/utils/errors';

import {useCurrentRevision, useQuestions} from 'client/common/hooks/revision';
import {useBoxContext, useBoxType} from 'client/common/hooks/box';
import {useIsSuperuser, useUserRoles} from 'client/common/hooks/user';
import {useAudienceCategories, useAudienceRequest} from 'client/common/hooks/audience';

const noop = async (): Promise<Errors> => {
    return Promise.resolve({});
};

export function useErrors(): Errors {
    const {data = {}} = useQuery(ERRORS_KEY, noop, {enabled: false, initialData: {}});

    // Вычищаем пустые поля-объекты из объекта с ошибками
    // Пример: {questions: {1: {}}} => {}
    return useMemo(() => cleanDeep(data), [data]);
}

export function useScrollToError(actionBefore?: () => void) {
    const errors = useErrors();
    const hasErrors = !isEmpty(errors);

    useEffect(() => {
        actionBefore?.();

        if (hasErrors) {
            setTimeout(scrollToError, 0);
        }
    }, [actionBefore, hasErrors]);
}

export function useCheckSize() {
    const slug = useSurveySlug();
    const boxType = useBoxType(slug);
    const request = useAudienceRequest(slug);
    const userRoles = useUserRoles();
    const categories = useAudienceCategories(slug);

    return useCallback(() => {
        if (!request.data || !categories.data) return false;

        const selectedCountry = AudienceTargeting.getSelectedCountry({
            request: request.data,
            boxType,
            categories: categories.data
        });

        const limits = new AudienceSizeDO({
            boxType,
            targetingCountry: selectedCountry,
            size: request.data.size,
            type: request.data.type,
            categories: request.data.categories,
            userRoles
        }).getValidLimits();

        const isValid = request.data.size <= limits.max && request.data.size >= limits.min;

        if (isValid) {
            unsetError('size');
        } else {
            setError('size', ValidationErrorType.InvalidSize);
        }

        return isValid;
    }, [boxType, categories.data, userRoles, request.data]);
}

export function useCheckErrors(slug: SurveySlug) {
    const revision = useCurrentRevision(slug);
    const request = useAudienceRequest(slug);
    const boxType = useBoxType(slug);
    const boxContext = useBoxContext(slug);
    const errors = useErrors();
    const isSuperuser = useIsSuperuser();
    const questions = useQuestions();

    // eslint-disable-next-line complexity
    return useCallback(() => {
        if (!revision.data || !request.data) return true;

        let hasNewErrors = false;

        if (!boxType || !SKIP_QUESTION_VALIDATION_BOX_TYPES.includes(boxType)) {
            if (validateQuestions(questions, request.data) === false) {
                hasNewErrors = true;
            }
        }

        if (
            boxType &&
            boxContext &&
            validateByBoxMap[boxType]?.(boxContext, revision.data, request.data) === false
        ) {
            hasNewErrors = true;
        }

        const result = hasNewErrors || !isEmpty(errors);

        /* для облегчения дебага выводим ошибки */
        if (result && isSuperuser) {
            // eslint-disable-next-line no-console
            console.debug({useCheckErrors: errors});
        }

        return result;
    }, [revision.data, request.data, boxType, boxContext, errors, isSuperuser, questions]);
}
